/* eslint-disable no-empty */
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface OrgContextType {
  orgId: string;
  loading: boolean;
  /** Name of the overridden org (only set during impersonation) */
  overrideOrgName: string | null;
  /** Logo URL of the overridden org */
  overrideOrgLogo: string | null;
  /** Master-admin: switch context to another org */
  setOrgOverride: (orgId: string, orgName: string, logoUrl?: string | null) => void;
  /** Clear override and return to home org */
  clearOrgOverride: () => void;
}

const OrgContext = createContext<OrgContextType>({
  orgId: "",
  loading: true,
  overrideOrgName: null,
  overrideOrgLogo: null,
  setOrgOverride: () => {},
  clearOrgOverride: () => {},
});

export const useOrg = () => useContext(OrgContext);

export const OrgProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [homeOrgId, setHomeOrgId] = useState("");
  const [overrideOrgId, setOverrideOrgId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("org_override_id");
    } catch {
      return null;
    }
  });
  const [overrideOrgName, setOverrideOrgName] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("org_override_name");
    } catch {
      return null;
    }
  });
  const [overrideOrgLogo, setOverrideOrgLogo] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("org_override_logo");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setHomeOrgId("");
      setLoading(false);
      return;
    }

    const fetchOrg = async () => {
      // 1. Try new organization_users table first (primary_org preference)
      const { data: ouData } = await supabase
        .from("organization_users")
        .select("organization_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("is_primary_org", { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (ouData?.organization_id) {
        setHomeOrgId(ouData.organization_id);
        setLoading(false);
        return;
      }

      // 2. Final fallback via RPC
      const { data: portalMember } = await supabase.rpc("get_portal_member", {
        p_org_id: null,
      });

      const fallbackOrgId = (portalMember as Record<string, any>)?.member?.org_id;
      setHomeOrgId(typeof fallbackOrgId === "string" ? fallbackOrgId : "");
      setLoading(false);
    };

    fetchOrg();
  }, [user]);

  // Clear override only on true sign-out (user goes from defined to null)
  const [prevUser, setPrevUser] = useState(user);
  // biome-ignore lint/correctness/useExhaustiveDependencies: prevUser is intentionally excluded — we only want to react to user changes
  useEffect(() => {
    if (prevUser && !user) {
      setOverrideOrgId(null);
      setOverrideOrgName(null);
      setOverrideOrgLogo(null);
      try {
        sessionStorage.removeItem("org_override_id");
        sessionStorage.removeItem("org_override_name");
        sessionStorage.removeItem("org_override_logo");
      } catch {}
    }
    if (prevUser !== user) {
      setPrevUser(user);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const setOrgOverride = useCallback((orgId: string, orgName: string, logoUrl?: string | null) => {
    setOverrideOrgId(orgId);
    setOverrideOrgName(orgName);
    setOverrideOrgLogo(logoUrl ?? null);
    try {
      sessionStorage.setItem("org_override_id", orgId);
      sessionStorage.setItem("org_override_name", orgName);
      if (logoUrl) sessionStorage.setItem("org_override_logo", logoUrl);
      else sessionStorage.removeItem("org_override_logo");
    } catch {}
  }, []);

  const clearOrgOverride = useCallback(() => {
    setOverrideOrgId(null);
    setOverrideOrgName(null);
    setOverrideOrgLogo(null);
    try {
      sessionStorage.removeItem("org_override_id");
      sessionStorage.removeItem("org_override_name");
      sessionStorage.removeItem("org_override_logo");
    } catch {}
  }, []);

  const orgId = overrideOrgId || homeOrgId;

  const value = useMemo(
    () => ({ orgId, loading, overrideOrgName, overrideOrgLogo, setOrgOverride, clearOrgOverride }),
    [orgId, loading, overrideOrgName, overrideOrgLogo, setOrgOverride, clearOrgOverride],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};
